import { ChangeDetectionStrategy, Component, Inject, OnDestroy } from '@angular/core';
import { SelectModule } from "primeng/select";
import { COMPANY_ROLES, TCompanyRole } from "../../models/company-role";
import { TRoleProductLine } from "@zaeper/core-lib";
import { ILanguageStateService } from "@zaeper/localization-lib";
import { languageStateServiceInjectionToken } from "../../services/language/languageStateServiceInjectionToken";
import { roleProductLines$ } from "../../global-states/session/role-product-lines";
import { RoleTextPipe } from "../../pipes/role-text.pipe";
import { loadActiveRole } from "../../global-states/session/load-active-role";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import {
	EPageLayout,
	ESeverity,
	ITab,
	IToastService,
	toastServiceInjectionToken,
	ToastMessage,
	PageLayoutComponent,
	IconComponent,
	TabNavigationComponent,
	TabNavigationStateService,
	tabNavigationStateServiceInjectionToken,
	CompareRolesDialogComponent
} from '@zaeper/angular-dashboard-view-lib';
import { ZDictionaryPipe } from "@zaeper/angular-localization-lib";
import { ZTemplateDirective } from "@zaeper/angular-core-lib";
import { ButtonModule } from "primeng/button";
import { TableModule } from "primeng/table";
import { TagModule } from "primeng/tag";
import { InputTextModule } from "primeng/inputtext";
import { TooltipModule } from "primeng/tooltip";
import { MenuItem } from "primeng/api";
import { BehaviorSubject, combineLatest, Observable, Subscription } from "rxjs";
import { sessionStateServiceInjectionToken } from "../../global-states/session/session-state-service-injection-token";
import { ISessionStateService } from "../../global-states/session/i-session-state.service";
import { companyRestClientInjectionToken } from "../../rest-clients/company/company-rest-client-injection-token";
import { ICompanyRestClient } from "../../rest-clients/company/i-company-rest.client";
import { ICompany } from "../../models/i-company";
import { IJoinCode } from "../../models/i-join-code";
import { IJoinRequest } from "../../models/i-join-request";
import { ICompanyInvitation } from "../../models/i-company-invitation";
import { ICompanyMember } from "../../models/i-company-member";

@Component({
	selector: 'app-company-management-page',
	templateUrl: './company-management-page.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		CommonModule,
		FormsModule,
		ButtonModule,
		TableModule,
		TagModule,
		TabNavigationComponent,
		InputTextModule,
		TooltipModule,
		SelectModule,
		ZDictionaryPipe,
		ZTemplateDirective,
		PageLayoutComponent,
		IconComponent,
		CompareRolesDialogComponent,
		RoleTextPipe
	],
	providers: [
		{
			provide: tabNavigationStateServiceInjectionToken,
			useClass: TabNavigationStateService
		}
	]
})
export class CompanyManagementPageComponent implements OnDestroy {
	public readonly ePageLayout: typeof EPageLayout = EPageLayout;

	public readonly navigation: MenuItem[] = [
		{
			icon: 'pi pi-home',
			routerLink: '/de/',
		}, {
			label: 'Unternehmen',
			routerLink: '/de/company',
		},
	];

	public readonly tabs: ITab[] = [
		{ title: "Mitglieder", subject: "members" },
		{ title: "Beitrittscode", subject: "join-code" },
		{ title: "Beitrittsanfragen", subject: "join-requests" },
		{ title: "Einladungen", subject: "invitations" }
	];

	public readonly activeCompany$: Observable<ICompany | null>;
	public readonly members$: BehaviorSubject<ICompanyMember[]> = new BehaviorSubject<ICompanyMember[]>([]);
	public readonly joinCode$: BehaviorSubject<IJoinCode | null> = new BehaviorSubject<IJoinCode | null>(null);
	public readonly joinRequests$: BehaviorSubject<IJoinRequest[]> = new BehaviorSubject<IJoinRequest[]>([]);
	public readonly invitations$: BehaviorSubject<ICompanyInvitation[]> = new BehaviorSubject<ICompanyInvitation[]>([]);
	public readonly isLoadingJoinCode$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	/** Join code, join requests, invitations and member changes are for company admins only. */
	public readonly isAdmin$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public inviteEmail: string = "";
	/** The role comparison next to the picker. */
	public isCompareRolesOpen: boolean = false;
	public readonly language$: Observable<string>;
	public readonly activeRole$: Observable<TCompanyRole | null>;
	public readonly productLines$: Observable<TRoleProductLine[]>;

	private readonly _subscriptions: Subscription = new Subscription();
	private _currentUserId: string | null = null;

	constructor(
		@Inject(sessionStateServiceInjectionToken) private readonly _sessionStateService: ISessionStateService,
		@Inject(companyRestClientInjectionToken) private readonly _companyRestClient: ICompanyRestClient,
		@Inject(toastServiceInjectionToken) private readonly _toastService: IToastService,
		@Inject(languageStateServiceInjectionToken) languageStateService: ILanguageStateService
	) {
		this.activeCompany$ = this._sessionStateService.activeCompany$;
		this.language$ = languageStateService.language$;
		this.activeRole$ = this._sessionStateService.activeRole$;
		this.productLines$ = roleProductLines$(this._sessionStateService);

		this._subscriptions.add(combineLatest([this.activeCompany$, this._sessionStateService.currentUser$]).subscribe(([company, currentUser]) => {
			this._currentUserId = currentUser?.id ?? null;
			if (company && currentUser) {
				this._loadMembers(company.id);
			}
		}));
	}

	ngOnDestroy(): void {
		this._subscriptions.unsubscribe();
	}

	public generateJoinCode(): void {
		const company = this._sessionStateService.getActiveCompany();
		if (!company) return;

		this.isLoadingJoinCode$.next(true);
		this._companyRestClient.generateJoinCode(company.id).then(joinCode => {
			this.joinCode$.next(joinCode);
			this.isLoadingJoinCode$.next(false);
			this._toastService.setMessage(new ToastMessage({ title: "Beitrittscode", detail: "Neuer Beitrittscode wurde generiert.", severity: ESeverity.success }));
		}).catch(() => {
			this.isLoadingJoinCode$.next(false);
		});
	}

	public acceptJoinRequest(joinRequest: IJoinRequest): void {
		const company = this._sessionStateService.getActiveCompany();
		if (!company) return;

		this._companyRestClient.acceptJoinRequest(company.id, joinRequest.id).then(() => {
			this._toastService.setMessage(new ToastMessage({ title: "Beitrittsanfrage", detail: "Anfrage wurde akzeptiert.", severity: ESeverity.success }));
			this._loadJoinRequests(company.id);
		});
	}

	public rejectJoinRequest(joinRequest: IJoinRequest): void {
		const company = this._sessionStateService.getActiveCompany();
		if (!company) return;

		this._companyRestClient.rejectJoinRequest(company.id, joinRequest.id).then(() => {
			this._toastService.setMessage(new ToastMessage({ title: "Beitrittsanfrage", detail: "Anfrage wurde abgelehnt.", severity: ESeverity.info }));
			this._loadJoinRequests(company.id);
		});
	}

	public sendInvitation(): void {
		const company = this._sessionStateService.getActiveCompany();
		if (!company || !this.inviteEmail.trim()) return;

		this._companyRestClient.invite({ companyId: company.id, email: this.inviteEmail.trim() }).then(() => {
			this._toastService.setMessage(new ToastMessage({ title: "Einladung", detail: `Einladung an ${this.inviteEmail} gesendet.`, severity: ESeverity.success }));
			this.inviteEmail = "";
			this._loadInvitations(company.id);
		});
	}

	public removeMember(userId: string): void {
		const company = this._sessionStateService.getActiveCompany();
		if (!company) return;

		this._companyRestClient.removeMember(company.id, userId).then(() => {
			this._toastService.setMessage(new ToastMessage({ title: "Mitglied", detail: "Mitglied wurde entfernt.", severity: ESeverity.success }));
			this._loadMembers(company.id);
		});
	}

	/** The roles an admin can give, lowest first (Viewer < Editor < Manager < Admin). */
	public readonly roleOptions: { value: TCompanyRole }[] = COMPANY_ROLES.map(value => ({ value }));

	public changeRole(userId: string, newRole: TCompanyRole): void {
		const company = this._sessionStateService.getActiveCompany();
		if (!company) return;

		this._companyRestClient.changeRole(company.id, userId, newRole).then(() => {
			this._toastService.setMessage(new ToastMessage({ title: "Mitglied", detail: "Rolle wurde geändert.", severity: ESeverity.success }));
		}).catch((error: unknown) => {
			// The last admin can't be demoted; the list shows the stored roles again.
			const lastAdmin: boolean = JSON.stringify((error as { error?: unknown })?.error ?? "").includes("last_admin");
			this._toastService.setMessage(new ToastMessage({
				title: "Mitglied",
				detail: lastAdmin ? "Die Organisation braucht mindestens einen Administrator." : "Rolle konnte nicht geändert werden.",
				severity: ESeverity.error
			}));
		}).finally(() => {
			this._loadMembers(company.id);
			void loadActiveRole(this._sessionStateService, this._companyRestClient, company.id);
		});
	}

	public isJoinCodeExpired(joinCode: IJoinCode): boolean {
		return joinCode.expiresAt < Date.now();
	}

	private _loadMembers(companyId: string): void {
		this._companyRestClient.getMembers(companyId).then(members => {
			this.members$.next(members);

			const isAdmin: boolean = members.some(member => member.userId === this._currentUserId && member.role === "ADMIN");
			this.isAdmin$.next(isAdmin);
			if (isAdmin) {
				this._loadJoinRequests(companyId);
				this._loadInvitations(companyId);
				this._loadActiveJoinCode(companyId);
			} else {
				this.joinRequests$.next([]);
				this.invitations$.next([]);
				this.joinCode$.next(null);
			}
		});
	}

	private _loadActiveJoinCode(companyId: string): void {
		this._companyRestClient.getActiveJoinCode(companyId).then(joinCode => {
			this.joinCode$.next(joinCode);
		}).catch(() => {
			this.joinCode$.next(null);
		});
	}

	private _loadJoinRequests(companyId: string): void {
		this._companyRestClient.getJoinRequests(companyId).then(requests => {
			this.joinRequests$.next(requests);
		});
	}

	private _loadInvitations(companyId: string): void {
		this._companyRestClient.getInvitations(companyId).then(invitations => {
			this.invitations$.next(invitations);
		});
	}
}
